import axios from 'axios'
import { API_BASE_URL_WITH_VERSION } from '../config/api'

const authAPI = axios.create({
  baseURL: API_BASE_URL_WITH_VERSION,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Types
export interface User {
  uid: string
  email: string
  name: string
  birthData?: {
    day: number
    month: number
    year: number
    hour?: number
    minute?: number
    latitude?: number
    longitude?: number
    place_of_birth?: string
  }
  preferences?: any
  profile?: UserProfile
}

export interface UserProfile {
  uid: string
  email: string
  name: string
  birthData: BirthData
  wellnessProfile?: any
  preferences?: any
  createdAt: string
  updatedAt: string
}

export interface BirthData {
  birthDate: string
  birthTime: string
  latitude: number
  longitude: number
  placeOfBirth: string
  timezone: number
}

export interface SignupData {
  email: string
  password: string
  name: string
  birthData: BirthData
}

export interface LoginData {
  email: string
  password: string
}

export interface AuthResponse {
  success: boolean
  user?: User
  token?: string
  error?: string
  message?: string
}

export interface UserStatus {
  success: boolean
  uid: string
  isOnline: boolean
  astroAgentsLive: number
  totalCharts: number
  statusMessage?: string
  retryAttempts?: number
  message: string
}

// Auth Service
export const authService = {
  // Sign up new user
  signUp: async (data: SignupData): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/signup', data)
      return response.data
    } catch (error: any) {
      console.error('Signup error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create account'
      }
    }
  },

  // Sign in user
  signIn: async (data: LoginData): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/signin', data)
      return response.data
    } catch (error: any) {
      console.error('Signin error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sign in'
      }
    }
  },

  // Sign out user
  signOut: async (): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/signout')
      return response.data
    } catch (error: any) {
      console.error('Signout error:', error)
      return {
        success: false,
        error: 'Failed to sign out'
      }
    }
  },

  // Get current user
  getCurrentUser: async (): Promise<AuthResponse> => {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        return {
          success: false,
          error: 'No authentication token found'
        }
      }

      // Check if this is a JWT token or a dev token
      let uid: string
      if (token.startsWith('dev_')) {
        // Development token - extract user ID
        uid = token.substring(4)
      } else {
        // JWT token - we need to decode it to get the user ID
        try {
          // Simple JWT decode (without verification)
          const payload = JSON.parse(atob(token.split('.')[1]))
          uid = payload.userId
        } catch (decodeError) {
          console.error('Failed to decode JWT token:', decodeError)
          return {
            success: false,
            error: 'Invalid authentication token'
          }
        }
      }
      
      const response = await authAPI.get(`/auth/me-simple?uid=${uid}`)
      return response.data
    } catch (error: any) {
      console.error('Get current user error:', error)
      return {
        success: false,
        error: 'Failed to get current user'
      }
    }
  },

  // Update user profile
  updateProfile: async (uid: string, updates: Partial<UserProfile>): Promise<AuthResponse> => {
    try {
      const response = await authAPI.put(`/auth/profile-simple`, { uid, updates })
      return response.data
    } catch (error: any) {
      console.error('Update profile error:', error)
      return {
        success: false,
        error: 'Failed to update profile'
      }
    }
  },

  // Reset password
  resetPassword: async (email: string): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/reset-password', { email })
      return response.data
    } catch (error: any) {
      console.error('Reset password error:', error)
      return {
        success: false,
        error: 'Failed to reset password'
      }
    }
  },

  // Verify token
  verifyToken: async (token: string): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/verify-token', { token })
      return response.data
    } catch (error: any) {
      console.error('Verify token error:', error)
      return {
        success: false,
        error: 'Invalid token'
      }
    }
  },

  // Get user online/offline status based on chart data with retry logic
  getUserStatus: async (uid: string, autoImport: boolean = false, retryCount: number = 0): Promise<UserStatus> => {
    const maxRetries = 2 // Reduced to 2 attempts
    const retryDelay = 2000 * (retryCount + 1) // Increased delay to avoid rate limiting
    
    try {
      const response = await authAPI.get(`/auth/user-status/${uid}?autoImport=${autoImport}`)
      return response.data
    } catch (error: any) {
      console.error(`Get user status error (attempt ${retryCount + 1}):`, error)
      
      // Handle rate limiting and retry
      if ((error.response?.status === 429 || error.response?.status === 503) && retryCount < maxRetries) {
        console.log(`Rate limited, retrying in ${retryDelay}ms...`)
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return authService.getUserStatus(uid, autoImport, retryCount + 1)
      }
      
      // Handle network errors and retry
      if (!error.response && retryCount < maxRetries) {
        console.log(`Network error, retrying in ${retryDelay}ms...`)
        await new Promise(resolve => setTimeout(resolve, retryDelay))
        return authService.getUserStatus(uid, autoImport, retryCount + 1)
      }
      
      // Final fallback after all retries
      const errorMessage = error.response?.status === 429 
        ? 'Rate limited. Please wait a moment before trying again.'
        : error.response?.status === 503
        ? 'Service temporarily unavailable. Please try again.'
        : 'Failed to check user status. Please try again.'
      
      return {
        success: false,
        uid,
        isOnline: false,
        astroAgentsLive: 0,
        totalCharts: 0,
        retryAttempts: retryCount + 1,
        message: errorMessage
      }
    }
  },

  // Activate Astro Agents
  activateAstroAgents: async (userId: string): Promise<AuthResponse> => {
    try {
      const response = await authAPI.post('/auth/activate-astro-agents', { userId })
      return response.data
    } catch (error: any) {
      console.error('Activate astro agents error:', error)
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to activate astro agents'
      }
    }
  }
}

// Add auth token to requests
authAPI.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Handle auth errors
authAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
) 